import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import {
  InMemoryScrollingFeature,
  InMemoryScrollingOptions,
  provideRouter,
  withEnabledBlockingInitialNavigation,
  withInMemoryScrolling,
} from '@angular/router';
import { appRoutes } from './app.routes';
import {
  provideClientHydration,
  withEventReplay,
} from '@angular/platform-browser';
import {
  Common,
  CommonRouterBasedService,
  CommonUrlBasedService,
  FileWebService,
  Hub,
  HubWebService,
  i18nService,
  MepFile,
  MepStorage,
  PlatformService,
  StorageWebService,
} from '@meplis/services';
import { appInitializer } from './app.initializer';
import { AuthenticationService } from './shared/services/application/authentication.service';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  HttpClientModule,
  provideHttpClient,
  withFetch,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { TokenInterceptor } from './shared/interceptors/token.interceptor';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  DynamicHtmlModule,
  FilepickerInsertComponent,
  SocialProfileLinkComponent,
  SocialHashtagLinkComponent,
  SocialExternalLinkComponent,
  FilesVideoComponent,
} from '@meplis/components';

const scrollConfig: InMemoryScrollingOptions = {
  scrollPositionRestoration: 'top',
  anchorScrolling: 'enabled',
};

const inMemoryScrollingFeature: InMemoryScrollingFeature =
  withInMemoryScrolling(scrollConfig);

export const appConfig: ApplicationConfig = {
  providers: [
    provideClientHydration(withEventReplay()),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      appRoutes,
      inMemoryScrollingFeature,
      withEnabledBlockingInitialNavigation()
    ),
    provideAnimations(),
    provideAppInitializer(() => {
      const initializerFn = appInitializer(
        inject(AuthenticationService),
        inject(i18nService),
        inject(TranslateService)
      );
      return initializerFn();
    }),
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true,
    },
    importProvidersFrom(
      TranslateModule.forRoot(),
      DynamicHtmlModule.forRoot({
        components: [
          {
            component: FilepickerInsertComponent,
            selector: 'filepicker-insert',
          },
          {
            component: SocialProfileLinkComponent,
            selector: 'mec-social-profile-link',
          },
          {
            component: SocialHashtagLinkComponent,
            selector: 'mec-social-hashtag-link',
          },
          {
            component: SocialExternalLinkComponent,
            selector: 'mec-social-external-link',
          },
          {
            component: FilesVideoComponent,
            selector: 'files-video',
          },
        ],
      })
    ),
    {
      provide: Common,
      useClass: CommonRouterBasedService,
    },
    {
      provide: MepStorage,
      deps: [PlatformService],
      useClass: StorageWebService,
    },
    {
      provide: MepFile,
      useClass: FileWebService,
    },
    {
      provide: Hub,
      useClass: HubWebService,
    },
  ],
};
